import React, { useCallback } from "react";
import PriorityMarker from "./PriorityMarker";
import StartDate from "./StartDate";
import TaskMenu from "./TaskMenu";
import { Task } from "records/Task";
// import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DueDate from "./DueDate";
import CardColumn from "components/CardColumn";
import addSentryBreadcrumb from "sentry/addSentryBreadcrumb";
import { Button, Checkbox } from "@alm/flow-ds/components";
import { useHistory } from "react-router-dom";
import { useGetConvertedUserTime } from "hooks/useTimezone";
import { getCheckedBoxes } from "data/app/appSelectors";
import { useSelector } from "react-redux";

interface Props extends Task {
	changePriority: (id: string, priority: Task["priority"]) => void;
	claimTask: (id: string | string[]) => void;
	isChecked: boolean;
	handleCheckboxClick: (id: string) => () => void;
	loading?: boolean;
}

function UnclaimedTaskCard({
	id,
	taskName,
	workflowDefinitionName,
	priority,
	taskDueDate,
	createdTime,
	changePriority,
	claimTask,
	isChecked,
	handleCheckboxClick,
	loading,
}: Props) {
	const { t } = useTranslation();

	const createdTimeConverted = useGetConvertedUserTime(createdTime);

	const taskDueDateConverted = useGetConvertedUserTime(taskDueDate);

	const history = useHistory();

	const checkboxes = useSelector(getCheckedBoxes);

	/**
	 * Changing the priority
	 */
	const handlePriorityChange = useCallback(
		(e: React.SyntheticEvent<HTMLButtonElement>) => {
			addSentryBreadcrumb({
				category: "button.click",
				message: "Change priority",
			});
			changePriority(id, e.currentTarget.value as Task["priority"]);
		},
		[changePriority, id]
	);

	/**
	 * Handle claiming a task
	 */
	const handleClaimTask = useCallback(() => {
		let breadcrumbMsg = "";

		if (checkboxes.length > 1) {
			claimTask(checkboxes);
			breadcrumbMsg = `Claim tasks: ${checkboxes
				.join(", ")
				.replace(/,\s*$/, "")}`;
		} else {
			claimTask(id);
			breadcrumbMsg = `Claim task ${id}`;
		}

		addSentryBreadcrumb({
			category: "button.click",
			message: breadcrumbMsg,
		});
	}, [claimTask, id, checkboxes]);

	/**
	 * Handle claiming and starting a task
	 */
	const handleClaimAndStartTask = useCallback(() => {
		history.push(`/claimtask/${id}`);
		addSentryBreadcrumb({
			category: "button.click",
			message: `Claim & Start task ${id}`,
		});
	}, [history, id]);

	return (
		<>
			<CardColumn width="none">
				<Checkbox
					id={`checkbox__${id}`}
					checked={isChecked}
					value={isChecked as unknown as string}
					onChange={handleCheckboxClick(id)}
					label=""
					ariaLabel={`Select task ${id}`}
					className="tasks-checkbox"
				/>
			</CardColumn>
			<CardColumn className="task-card__title--unclaimed" width="xl">
				{taskName}
			</CardColumn>
			<CardColumn className="task-card__activity" width="xl">
				{workflowDefinitionName}
			</CardColumn>
			<CardColumn className="task-card__date--start" width="sm">
				<StartDate date={createdTimeConverted} />
			</CardColumn>
			<CardColumn className="task-card__date--due" width="sm">
				<DueDate date={taskDueDateConverted} />
			</CardColumn>
			<CardColumn className="task-card__priority" width="xs">
				<PriorityMarker priority={priority} />
			</CardColumn>
			<CardColumn className="task-card__menu">
				<TaskMenu
					changePriority={handlePriorityChange}
					id={id}
					prioritySet={priority !== "NORMAL"}
					canBeReassigned={false}
					canBeClaimed={true}
					claimTask={handleClaimTask}
					loading={loading}
				/>
			</CardColumn>
			<CardColumn className="task-card__claim" width="sm">
				<div className="d-grid">
					<Button
						onClick={handleClaimAndStartTask}
						className="btn btn-outline-primary task-card__claim-btn"
						label={t("main.tasks.labels.claimAndStart")}
					/>
				</div>
			</CardColumn>
		</>
	);
}

export default UnclaimedTaskCard;
